/**
 * SpeakingPage.jsx — Student
 * DeutschBridge Institute
 *
 * Two submission modes per exam:
 *   1. WhatsApp — opens wa.me with pre-filled message; records submission on confirm
 *   2. TikTok URL — paste link and submit
 *
 * Shows grade/feedback after grading, "Pending Review" while waiting.
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { Spinner } from '../../components/shared/UI';
import API from '../../utils/apiClient';

// ── Brand ─────────────────────────────────────────────────────
const R = '#DD0000';
const B = '#1A1A1A';
const G = '#FFCE00';
const WA_GREEN = '#25D366';
const WA_NUMBER = '233202071417';

// ── Helpers ───────────────────────────────────────────────────
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  });
}
function isOverdue(due) {
  if (!due) return false;
  return new Date(due) < new Date();
}
function extractTikTokId(url) {
  const m = url.match(/tiktok\.com\/@[\w.]+\/video\/(\d+)/);
  return m ? m[1] : null;
}
function buildWaLink(studentName, title, studentId) {
  const msg = `Hi! I am ${studentName} submitting my speaking exam for: ${title}. Student ID: ${studentId}`;
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`;
}

// ── Level badge ───────────────────────────────────────────────
function LevelBadge({ level }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '2px 10px', borderRadius: 20,
      background: R, color: '#fff',
      fontSize: 11, fontWeight: 800, letterSpacing: '0.5px',
    }}>
      {level}
    </span>
  );
}

// ── WhatsApp icon (SVG) ───────────────────────────────────────
function WhatsAppIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────
// ExamCard
// ─────────────────────────────────────────────────────────────
function ExamCard({ exam, studentName, studentId, onSubmitted }) {
  const { addToast } = useToast();

  const sub = exam.mySubmission;

  const [tiktokUrl, setTiktokUrl]     = useState('');
  const [tiktokValid, setTiktokValid] = useState(false);
  const [submittingTt, setSubmittingTt] = useState(false);
  const [submitted, setSubmitted]     = useState(!!sub);
  const [localSub, setLocalSub]       = useState(sub);
  // Track whether the student has already clicked the WA button this session
  const [waSent, setWaSent]           = useState(sub?.whatsapp_submitted === 1);

  const overdue = isOverdue(exam.due_date);
  const waLink  = buildWaLink(studentName, exam.title, studentId);

  const handleTiktokChange = (v) => {
    setTiktokUrl(v);
    setTiktokValid(!!extractTikTokId(v));
  };

  // Called when student clicks the WhatsApp button.
  // Opens WA in a new tab AND records the submission immediately.
  const handleWhatsAppClick = async () => {
    window.open(waLink, '_blank', 'noopener,noreferrer');
    setWaSent(true);
    // Record submission on the backend (fire-and-forget-ish)
    try {
      const { data } = await API.post(`/speaking/exams/${exam.id}/submit`, {
        whatsapp_submitted: true,
      });
      const newSub = {
        sub_id:             data.submissionId,
        whatsapp_submitted: 1,
        tiktok_url:         null,
        submission_type:    'whatsapp',
        submitted_at:       new Date().toISOString(),
        score:              null,
        feedback:           null,
      };
      setLocalSub(newSub);
      setSubmitted(true);
      addToast('WhatsApp submission recorded! ✅', 'success');
      onSubmitted?.();
    } catch (e) {
      // Don't block the WA link even if backend fails
      console.error('[speaking] WA submit error:', e.message);
    }
  };

  // Called when student submits a TikTok URL
  const handleTiktokSubmit = async () => {
    if (!tiktokValid) return;
    setSubmittingTt(true);
    try {
      const { data } = await API.post(`/speaking/exams/${exam.id}/submit`, {
        tiktok_url:         tiktokUrl.trim(),
        whatsapp_submitted: waSent,   // include WA if already sent
      });
      const newSub = {
        sub_id:             data.submissionId,
        whatsapp_submitted: waSent ? 1 : 0,
        tiktok_url:         tiktokUrl.trim(),
        submission_type:    waSent ? 'whatsapp+tiktok' : 'tiktok',
        submitted_at:       new Date().toISOString(),
        score:              null,
        feedback:           null,
      };
      setLocalSub(newSub);
      setSubmitted(true);
      addToast('TikTok link submitted! 🎵', 'success');
      onSubmitted?.();
    } catch (e) {
      addToast(e.response?.data?.error || 'Submission failed. Please try again.', 'error');
    } finally {
      setSubmittingTt(false);
    }
  };

  return (
    <div style={{
      background: '#fff',
      borderRadius: 14,
      border: '1px solid #E5E7EB',
      overflow: 'hidden',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      marginBottom: 20,
    }}>

      {/* ── Card header ── */}
      <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid #F3F4F6' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
              <LevelBadge level={exam.level} />
              {submitted && localSub && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '3px 10px', borderRadius: 20,
                  background: localSub.score != null ? '#F0FDF4' : '#FFFBEB',
                  border: `1px solid ${localSub.score != null ? '#86EFAC' : '#FCD34D'}`,
                  color:   localSub.score != null ? '#15803D'  : '#92400E',
                  fontSize: 12, fontWeight: 700,
                }}>
                  {localSub.score != null ? '✓ Graded' : '⏳ Pending Review'}
                </span>
              )}
            </div>

            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: B, lineHeight: 1.3 }}>
              {exam.title}
            </h3>

            {exam.instructions && (
              <p style={{ margin: '6px 0 0', fontSize: 13, color: '#374151', lineHeight: 1.55,
                background: '#F9FAFB', borderRadius: 8, padding: '8px 12px', border: '1px solid #E5E7EB' }}>
                📋 {exam.instructions}
              </p>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 20, marginTop: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: overdue ? R : '#6B7280', fontWeight: overdue ? 700 : 500 }}>
            {overdue ? '⚠️ ' : '📅 '}Due: {fmtDate(exam.due_date)}
          </span>
          <span style={{ fontSize: 12, color: '#6B7280' }}>🏆 Max: {exam.max_score} pts</span>
          <span style={{ fontSize: 12, color: '#6B7280' }}>🎤 Speaking Exam</span>
        </div>
      </div>

      {/* ── Submitted state ── */}
      {submitted && localSub ? (
        <div style={{ padding: '18px 20px' }}>

          {/* What was submitted */}
          <div style={{ padding: '14px 16px', background: '#F0FDF4', borderRadius: 10, border: '1px solid #BBF7D0', marginBottom: 16 }}>
            <p style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 700, color: '#15803D', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              ✅ Submitted — {fmtDate(localSub.submitted_at)}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(localSub.whatsapp_submitted === 1 || localSub.whatsapp_submitted === true) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: WA_GREEN, fontSize: 18 }}><WhatsAppIcon size={18} /></span>
                  <span style={{ fontSize: 13, color: B, fontWeight: 600 }}>Sent via WhatsApp to 0202071417</span>
                </div>
              )}
              {localSub.tiktok_url && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <span style={{ fontSize: 16, flexShrink: 0 }}>🎵</span>
                  <a href={localSub.tiktok_url} target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: 13, color: R, wordBreak: 'break-all' }}>
                    {localSub.tiktok_url}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Grade / feedback */}
          {localSub.score != null ? (
            <div style={{ padding: '14px 16px', background: '#EFF6FF', borderRadius: 10, border: '1px solid #BFDBFE' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: localSub.feedback ? 10 : 0 }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#1E40AF' }}>📊 Your Grade</p>
                <span style={{ fontSize: 22, fontWeight: 900, color: R, fontFamily: 'Syne, sans-serif' }}>
                  {localSub.score}
                  <span style={{ fontSize: 14, fontWeight: 500, color: '#6B7280' }}>/{exam.max_score}</span>
                </span>
              </div>
              {localSub.feedback && (
                <p style={{ margin: 0, fontSize: 13, color: '#1E3A8A', lineHeight: 1.6,
                  paddingTop: 10, borderTop: '1px solid #BFDBFE' }}>
                  💬 {localSub.feedback}
                </p>
              )}
            </div>
          ) : (
            <p style={{ margin: 0, fontSize: 13, color: '#92400E', background: '#FFFBEB',
              padding: '10px 14px', borderRadius: 8, border: '1px solid #FCD34D' }}>
              ⏳ Your submission is awaiting review from your teacher.
            </p>
          )}

          {/* Allow update if not yet graded */}
          {localSub.score == null && (
            <button onClick={() => setSubmitted(false)} style={{
              marginTop: 12, background: 'none', border: `1px solid ${R}`, color: R,
              borderRadius: 8, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}>
              ✏️ Update Submission
            </button>
          )}
        </div>

      ) : (
        /* ── Submission form ── */
        <div style={{ padding: '18px 20px' }}>

          {/* Option 1: WhatsApp */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 22, height: 22, borderRadius: '50%', background: R, color: '#fff',
                fontSize: 11, fontWeight: 800, flexShrink: 0 }}>1</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: B }}>Send via WhatsApp</span>
              <span style={{ fontSize: 11, color: '#6B7280', fontWeight: 400 }}>(recommended)</span>
            </div>

            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleWhatsAppClick}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                width: '100%', padding: '14px 20px',
                background: WA_GREEN, color: '#fff',
                borderRadius: 11, textDecoration: 'none',
                fontSize: 15, fontWeight: 700,
                boxShadow: '0 4px 16px rgba(37,211,102,0.3)',
                transition: 'opacity 0.2s',
                boxSizing: 'border-box',
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              <WhatsAppIcon size={22} />
              Send Speaking Recording via WhatsApp
            </a>

            <p style={{ margin: '8px 0 0', fontSize: 12, color: '#6B7280', textAlign: 'center', lineHeight: 1.5 }}>
              Record your speaking, save the video, then send it via WhatsApp to{' '}
              <strong>0202071417</strong>
            </p>

            {/* Message preview */}
            <details style={{ marginTop: 10 }}>
              <summary style={{ fontSize: 12, color: '#9CA3AF', cursor: 'pointer', userSelect: 'none' }}>
                Preview pre-filled message ▾
              </summary>
              <div style={{ marginTop: 8, padding: '10px 12px', background: '#F0FDF4', borderRadius: 8,
                border: '1px solid #BBF7D0', fontSize: 12, color: '#374151', lineHeight: 1.55 }}>
                "Hi! I am <strong>{studentName}</strong> submitting my speaking exam for: <strong>{exam.title}</strong>. Student ID: <strong>{studentId}</strong>"
              </div>
            </details>

            {waSent && (
              <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 12px', background: '#F0FDF4', borderRadius: 8,
                border: '1px solid #BBF7D0' }}>
                <span style={{ color: WA_GREEN }}><WhatsAppIcon size={16} /></span>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#15803D' }}>
                  WhatsApp opened — don't forget to send your recording!
                </span>
              </div>
            )}
          </div>

          {/* Divider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
            <span style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 600 }}>OR</span>
            <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
          </div>

          {/* Option 2: TikTok */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 22, height: 22, borderRadius: '50%', background: R, color: '#fff',
                fontSize: 11, fontWeight: 800, flexShrink: 0 }}>2</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: B }}>Submit TikTok Link</span>
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="url"
                value={tiktokUrl}
                onChange={e => handleTiktokChange(e.target.value)}
                placeholder="https://www.tiktok.com/@username/video/..."
                style={{
                  flex: 1, padding: '11px 14px', borderRadius: 10,
                  border: `2px solid ${tiktokUrl ? (tiktokValid ? '#22C55E' : '#F87171') : '#E5E7EB'}`,
                  fontSize: 14, outline: 'none', fontFamily: 'inherit',
                  transition: 'border-color 0.2s',
                }}
              />
              <button
                onClick={handleTiktokSubmit}
                disabled={!tiktokValid || submittingTt}
                style={{
                  flexShrink: 0, padding: '0 18px', borderRadius: 10, border: 'none',
                  background: tiktokValid && !submittingTt ? R : '#D1D5DB',
                  color: tiktokValid && !submittingTt ? '#fff' : '#9CA3AF',
                  fontSize: 13, fontWeight: 700,
                  cursor: tiktokValid && !submittingTt ? 'pointer' : 'not-allowed',
                  display: 'flex', alignItems: 'center', gap: 6,
                  transition: 'background 0.2s', minHeight: 46,
                }}
              >
                {submittingTt ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                    style={{ animation: 'db-spin 0.8s linear infinite' }}>
                    <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="3" strokeDasharray="40 20" />
                  </svg>
                ) : '🎵'} Submit
              </button>
            </div>

            {tiktokUrl && !tiktokValid && (
              <p style={{ margin: '4px 0 0', fontSize: 11, color: '#EF4444' }}>
                Enter a valid TikTok video URL (e.g. tiktok.com/@user/video/123...)
              </p>
            )}
            {tiktokValid && (
              <p style={{ margin: '4px 0 0', fontSize: 11, color: '#22C55E', fontWeight: 600 }}>
                ✓ Valid TikTok URL
              </p>
            )}

            <p style={{ margin: '8px 0 0', fontSize: 12, color: '#9CA3AF', lineHeight: 1.5 }}>
              Post your speaking video on TikTok, copy the link, and paste it here.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// Main page
// ═════════════════════════════════════════════════════════════
export default function SpeakingPage() {
  const { user } = useAuth();
  const { addToast } = useToast();

  const [exams, setExams]   = useState([]);
  const [loading, setLoading] = useState(true);

  const studentName = user?.name || 'Student';
  const studentId   = user?.student?.student_number || user?.studentId || '—';

  const load = () => {
    setLoading(true);
    API.get('/speaking/exams')
      .then(({ data }) => setExams(data.exams || []))
      .catch(() => addToast('Failed to load speaking exams', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '32px 16px', fontFamily: 'Space Grotesk, sans-serif' }}>
      <style>{`@keyframes db-spin { from{transform:rotate(0deg);} to{transform:rotate(360deg);} }`}</style>

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: B }}>🎤 Speaking Exams</h1>
        <p style={{ margin: '4px 0 0', fontSize: 14, color: '#6B7280' }}>
          Record your speaking and submit via WhatsApp or TikTok.
        </p>
      </div>

      {/* How it works guide */}
      <div style={{ padding: '14px 18px', background: '#FFFBEB', borderRadius: 12, border: `1px solid ${G}`, marginBottom: 24 }}>
        <p style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700, color: B }}>📖 How to submit your speaking:</p>
        <ol style={{ margin: 0, paddingLeft: 18 }}>
          {[
            'Read your speaking exam instructions below.',
            'Record yourself speaking on your phone (video or audio).',
            'Send via WhatsApp — click the green button which opens WhatsApp with your details pre-filled.',
            'Or post on TikTok and paste the link in the TikTok field.',
          ].map((step, i) => (
            <li key={i} style={{ fontSize: 13, color: '#374151', marginBottom: 4, lineHeight: 1.5 }}>{step}</li>
          ))}
        </ol>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
          <Spinner size="lg" className="text-brand-500" />
        </div>
      ) : exams.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: '#fff', borderRadius: 14, border: '1px solid #E5E7EB' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🎤</div>
          <p style={{ margin: 0, fontWeight: 700, fontSize: 15, color: B }}>No speaking exams yet</p>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: '#9CA3AF' }}>Your teacher hasn't assigned any speaking exams yet.</p>
        </div>
      ) : (
        exams.map(exam => (
          <ExamCard
            key={exam.id}
            exam={exam}
            studentName={studentName}
            studentId={studentId}
            onSubmitted={load}
          />
        ))
      )}
    </div>
  );
}
